<script setup lang="ts">
/**
 * "Offline · showing what you had": a calm glass pill under the status bar while the network is gone, and a short
 * "Back online" when it returns. Taps pass through it (it sits over page headers). Native and phone widths only;
 * desktop is unchanged. Icons are inline SVG: the icon font is a network load and may be missing offline.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { haptic } from './haptics'
import { useNetworkStatus } from './network'
import { useTopIsland } from './topIsland'
import { isNativePlatform } from '@/utils/platform'

const BACK_ONLINE_MS = 1800

const { online } = useNetworkStatus()
const isPhone = useMediaQuery('(max-width: 639px)')
const enabled = computed(() => isNativePlatform() || isPhone.value)
const state = ref<'hidden' | 'offline' | 'back'>(online.value ? 'hidden' : 'offline')
let backTimer: ReturnType<typeof setTimeout> | undefined

watch(online, (up) => {
    clearTimeout(backTimer)
    if (!up) {
        state.value = 'offline'
        haptic('warning')
        return
    }
    if (state.value !== 'offline') return
    state.value = 'back'
    backTimer = setTimeout(() => {
        state.value = 'hidden'
    }, BACK_ONLINE_MS)
})

onBeforeUnmount(() => clearTimeout(backTimer))

/** Under the call pill when one shows (the top island), above any banner. */
const islandOffset = useTopIsland('offline', () => (enabled.value && state.value !== 'hidden' ? 30 : 0))
</script>

<template>
    <Teleport to="body">
        <Transition name="offline-pill">
            <div
                v-if="enabled && state !== 'hidden'"
                class="offline-layer"
                :style="islandOffset ? { transform: `translate3d(0, ${islandOffset}px, 0)` } : undefined"
                role="status"
                aria-live="polite"
            >
                <div class="offline-pill" :class="{ 'is-back': state === 'back' }">
                    <svg v-if="state === 'offline'" class="offline-icon" viewBox="0 0 20 20" width="15" height="15" fill="none" aria-hidden="true">
                        <path d="M2.5 7.6a11 11 0 0 1 4.4-2.6M10 4.6a11 11 0 0 1 7.5 3M5 10.4a7 7 0 0 1 2.9-1.7M12.6 9a7 7 0 0 1 2.4 1.4M7.6 13.1a3.5 3.5 0 0 1 4.8 0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                        <circle cx="10" cy="16" r="1.2" fill="currentColor" />
                        <path d="M3.5 3.5l13 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                    </svg>
                    <span v-else class="online-dot" aria-hidden="true" />
                    <span v-if="state === 'offline'">Offline <span class="sep">·</span> showing what you had</span>
                    <span v-else>Back online</span>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.offline-layer {
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
    left: 0;
    right: 0;
    z-index: var(--z-toast, 10080);
    display: flex;
    justify-content: center;
    pointer-events: none;
    transition: transform var(--lh-dur-base, 240ms) var(--lh-ease-ios, cubic-bezier(0.32, 0.72, 0, 1));
}

.offline-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 30px;
    padding: 0 13px 0 11px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.005em;
    white-space: nowrap;
    color: var(--lh-text-1, #242424);
    background: rgba(250, 250, 250, 0.8);
    -webkit-backdrop-filter: blur(20px) saturate(1.8);
    backdrop-filter: blur(20px) saturate(1.8);
    box-shadow:
        0 0 0 0.5px rgba(36, 36, 36, 0.09),
        0 6px 18px rgba(36, 36, 36, 0.12),
        inset 0 0 0 0.5px rgba(255, 255, 255, 0.6);
    transition: color var(--lh-dur-fast, 150ms) ease;
}
html.dark .offline-pill {
    color: var(--lh-text-1, #e7e5e4);
    background: rgba(41, 37, 36, 0.82);
    box-shadow:
        0 0 0 0.5px rgba(255, 255, 255, 0.1),
        0 8px 20px rgba(0, 0, 0, 0.45),
        inset 0 0 0 0.5px rgba(255, 255, 255, 0.06);
}

.offline-icon {
    flex: none;
    color: var(--lh-text-3, #737373);
}
.sep {
    color: var(--lh-text-4, #a3a3a3);
    margin: 0 1px;
}
.online-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
}

.offline-pill-enter-active {
    transition:
        transform var(--lh-dur-push, 340ms) var(--lh-spring-soft, cubic-bezier(0.34, 1.3, 0.64, 1)),
        opacity var(--lh-dur-fast, 150ms) ease;
}
.offline-pill-leave-active {
    transition:
        transform var(--lh-dur-base, 240ms) var(--lh-ease-in, cubic-bezier(0.4, 0, 1, 1)),
        opacity var(--lh-dur-base, 240ms) ease;
}
.offline-pill-enter-from,
.offline-pill-leave-to {
    transform: translate3d(0, -12px, 0) scale(0.96);
    opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
    .offline-pill-enter-from,
    .offline-pill-leave-to {
        transform: none;
    }
}
</style>
